import { getAuthToken } from '../../utils/auth';
import {
  searchReportingFetchJSON as safeFetchJSON,
  searchReportingLegacyUrl,
} from '../../lib/flags/search-reporting';

export async function queryContacts(
  jwtToken,
  merchants,
  amountAtLeast,
  amountNoMoreThan,
  amountSpecific,
  filteredContactList,
  limit,
  searchAfter = undefined,
  filteredYearList = [],
  filteredTaxDeductible = [],
  merchantTimezone = 'America/Chicago',
  // Sync-error filter: an array of PF-rejection categories ('email'/'phone')
  // → sent as `syncErrorTypes` → OS `terms` on
  // `pocketflow_sync_error_fields.keyword` (matches contacts rejected on ANY
  // selected category). Empty array / falsy (default) → no sync-error filter.
  syncErrorTypes = [],
  // Contact tags filter (PocketFlows labels). Matches contacts that have
  // ANY of the given tags (OS `terms` on `tags.keyword`).
  filteredTagsList = [],
  // Donation Impact (PE-1607): contact ids resolved from broadcast/segment
  // membership. AND'd server-side as a separate id.keyword terms clause. When
  // the broadcast/segment filter is active but resolves to nothing, the caller
  // passes ['__no_match__'] so the table shows no contacts (not all).
  membershipIds = [],
  // Per-channel consent filter (communication-consent-plan.md U14g, Option B).
  // Arrays of enum values (SUBSCRIBED|UNSUBSCRIBED|NEVER_SUBSCRIBED|UNKNOWN) →
  // sent as `emailConsent`/`smsConsent` → OS `terms` on the respective
  // `.keyword` field (matches contacts whose channel status is ANY selected
  // value; email + sms intersect when both set). Empty (default) → no filter.
  // NOTE (skew): OS rows indexed before the consent updater deployed lack these
  // fields and won't match a terms clause until the OpenSearch resync
  // (release-plan BF-OS) runs — the filter under-matches old rows until then.
  emailConsentValues = [],
  smsConsentValues = [],
  // Location filter (PE-1869): arrays of RAW address values (every case
  // variant of each selected option — see geoFilterOptions.js) → OS `terms`
  // on addresses.country/region/city `.keyword` fields. Any-address match:
  // a contact matches if ANY of its addresses matches each sent level.
  countryValues = [],
  stateValues = [],
  cityValues = [],
  // Donor-status filter (dashboard-import-tool SPEC §10.1, PE-1913).
  // 'non_donor' → OS term on totalTransactionCount:0 (a COMPUTED state —
  // contacts missing the aggregate are excluded, absent ≠ zero);
  // 'donor' → count > 0. ''/undefined (default) → no filter ("All").
  donorStatus = '',
) {
  // Extract emails, names, and contact IDs from filteredContactList
  // filteredContactList now contains "email||name||id" format
  const filteredEmails = [];
  const filteredNames = [];
  const filteredContactIDs = [];

  filteredContactList
    .filter((contact) => contact && contact.trim() !== '')
    .forEach((contact) => {
      const parts = contact.split('||');
      if (parts.length >= 3) {
        filteredEmails.push(parts[0]); // email
        filteredNames.push(parts[1]); // name
        filteredContactIDs.push(parts[2]); // id
      }
    });

  const result = await safeFetchJSON(
    `${searchReportingLegacyUrl}/contacts`,
    {
      method: 'POST',
      headers: {
        Authorization: await getAuthToken(),
      },
      body: JSON.stringify({
        query: 'contacts',
        filters: {
          amountEquals: amountSpecific === '' ? '-1' : amountSpecific,
          amountGreaterThan: amountAtLeast === '' ? '-1' : amountAtLeast,
          amountLessThan: amountNoMoreThan === '' ? '-1' : amountNoMoreThan,
          merchantNames: merchants,
          names: filteredNames,
          emails: filteredEmails,
          ids: filteredContactIDs,
          limit: limit,
          years: filteredYearList,
          taxDeductible: filteredTaxDeductible,
          timezone: merchantTimezone,
          ...(searchAfter !== undefined && { searchAfter }),
          // Sync-error filter — the typed `terms` view. Omitted entirely when
          // empty so we don't expand the body for callers that don't use it.
          ...(Array.isArray(syncErrorTypes) && syncErrorTypes.length > 0 ? { syncErrorTypes } : {}),
          ...(filteredTagsList.length > 0 && { tags: filteredTagsList }),
          ...(membershipIds.length > 0 && { membershipIds }),
          ...(Array.isArray(emailConsentValues) && emailConsentValues.length > 0
            ? { emailConsent: emailConsentValues }
            : {}),
          ...(Array.isArray(smsConsentValues) && smsConsentValues.length > 0
            ? { smsConsent: smsConsentValues }
            : {}),
          ...(Array.isArray(countryValues) && countryValues.length > 0
            ? { countries: countryValues }
            : {}),
          ...(Array.isArray(stateValues) && stateValues.length > 0 ? { states: stateValues } : {}),
          ...(Array.isArray(cityValues) && cityValues.length > 0 ? { cities: cityValues } : {}),
          ...(donorStatus === 'donor' || donorStatus === 'non_donor' ? { donorStatus } : {}),
        },
      }),
    },
    {
      context: 'queryContacts',
      retries: 2,
      retryDelay: 1000,
    },
  );

  if (!result.ok) {
    console.error('[ContactsQueries] queryContacts failed:', result.error);
    return [];
  }

  return result.data || [];
}

// Distinct Country → State → City option tree for the contacts Location
// filter (PE-1869). Served by the same /contacts lambda from a terms
// aggregation on the contacts index, so the options always agree with what
// the filter can match. Returns {countries, allStates, allCities} of RAW
// values (case variants included) or null on failure.
export async function queryContactGeoOptions(merchants) {
  const result = await safeFetchJSON(
    `${searchReportingLegacyUrl}/contacts`,
    {
      method: 'POST',
      headers: {
        Authorization: await getAuthToken(),
      },
      body: JSON.stringify({
        query: 'contactGeoOptions',
        filters: {
          merchantNames: merchants,
        },
      }),
    },
    {
      context: 'queryContactGeoOptions',
      retries: 2,
      retryDelay: 1000,
    },
  );

  if (!result.ok) {
    console.error('[ContactsQueries] queryContactGeoOptions failed:', result.error);
    return null;
  }

  return result.data || null;
}

export async function queryContactsTopContacts(
  jwtToken,
  merchants,
  amountAtLeast,
  amountNoMoreThan,
  amountSpecific,
  keywordSearchTerm,
) {
  const result = await safeFetchJSON(
    `${searchReportingLegacyUrl}/contacts`,
    {
      method: 'POST',
      headers: {
        Authorization: await getAuthToken(),
      },
      body: JSON.stringify({
        query: 'topContacts',
        filters: {
          amountEquals: amountSpecific === '' ? '-1' : amountSpecific,
          amountGreaterThan: amountAtLeast === '' ? '-1' : amountAtLeast,
          amountLessThan: amountNoMoreThan === '' ? '-1' : amountNoMoreThan,
          merchantNames: merchants,
          keyword: keywordSearchTerm,
        },
      }),
    },
    {
      context: 'queryContactsTopContacts',
      retries: 2,
      retryDelay: 1000,
    },
  );

  if (!result.ok) {
    console.error('[ContactsQueries] queryContactsTopContacts failed:', result.error);
    return [];
  }

  return result.data || [];
}
